'use client'

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Share2, Edit, Trash2 } from "lucide-react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useAuth } from "@/hooks/use-auth"
import { toast } from "sonner"
import { ROLES } from "@/lib/constants"

interface ArticleActionsProps {
  articleId: string
  articleSlug: string
  articleTitle: string
  publishedDateTime: string
}

export function ArticleActions({ articleId, articleSlug, articleTitle, publishedDateTime }: ArticleActionsProps) {
  const router = useRouter()
  const { user, hasRole } = useAuth()
  const isAdmin = user && (hasRole(ROLES.ROOT_ADMIN) || hasRole(ROLES.SITE_ADMIN) || hasRole(ROLES.NEWS_EDITOR))
  const [deleting, setDeleting] = useState(false)

  const handleShare = async () => {
    const url = `${window.location.origin}/news/${articleSlug}`
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: articleTitle,
          url: url,
        })
      } catch (error) {
        // Fall back to clipboard
        fallbackShare(url)
      }
    } else {
      fallbackShare(url)
    }
  }

  const fallbackShare = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copied to clipboard!')
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
      toast.error('Failed to copy link')
    }
  }

  const handleDelete = async () => {
    try {
      setDeleting(true)
      
      const response = await fetch(`/api/news/${articleId}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete article')
      }

      toast.success('Article deleted successfully')
      router.push('/news')
    } catch (error) {
      console.error('Error deleting article:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to delete article')
    } finally {
      setDeleting(false)
    }
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="pt-8 border-t">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Published on {formatDateTime(publishedDateTime)}
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" asChild>
                <Link href={`/admin/news/${articleId}/edit`}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Article
                </Link>
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Article</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{articleTitle}"? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      disabled={deleting}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleting ? 'Deleting...' : 'Delete Article'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          <Button variant="outline" size="sm" onClick={handleShare}>
            <Share2 className="w-4 h-4 mr-2" />
            Share Article
          </Button>
        </div>
      </div>
    </div>
  )
}